/**
 * Derived performance metrics for the Infrastructure Comparison table.
 *
 * We support two cost formulas:
 * - Classic tasks (classification / NER): throughput is requests/sec, so we
 *   multiply by avg input tokens/request to get token throughput.
 * - Generative tasks (summarization): throughput is already tokens/sec.
 */

/**
 * Throughput (Requests/Sec) for a deployment serving a fixed
 * `concurrency_level` whose average per-request latency is `avgLatencyMs`.
 *
 *   throughput = concurrency / avg_latency_seconds
 *              = concurrency * 1000 / avg_latency_ms
 *
 * @param {Object} params
 * @param {number|null|undefined} params.avgLatencyMs
 * @param {number|null|undefined} params.concurrencyLevel
 * @returns {number|null} Requests per second, or null if either input is missing/zero.
 */
export function calculateThroughputRps({ avgLatencyMs, concurrencyLevel }) {
  if (!avgLatencyMs || !concurrencyLevel) return null;
  return (concurrencyLevel * 1000) / avgLatencyMs;
}

/**
 * Convert a `StatRow[]` of latency values (ms) into a `StatRow[]` of
 * throughput values (rps), assuming a fixed `concurrencyLevel`.
 *
 * Per-stat throughput is computed from the corresponding per-stat latency
 * with `throughput = concurrency / latency_seconds`. Because lower latency
 * yields higher throughput, the high-tail latency stats (Max / P95 / P90)
 * naturally land at the bottom of the throughput distribution — but we keep
 * the row order/keys identical to the latency table so the two tables read
 * 1:1 side-by-side and reuse `PerformanceStatsTable` as-is.
 *
 * Both `current` and `comparison` columns are converted when present so the
 * existing comparison-merge path keeps working unchanged.
 *
 * @param {Array<{key:string, metric:string, current:?number, comparison:?number, unit:string}>} latencyRows
 * @param {number|null|undefined} concurrencyLevel
 * @returns {Array<{key:string, metric:string, current:?number, comparison:?number, unit:string}>}
 */
export function deriveThroughputRowsFromLatency(latencyRows, concurrencyLevel) {
  if (!Array.isArray(latencyRows) || !concurrencyLevel) return [];
  const toRps = (latencyMs) =>
    calculateThroughputRps({ avgLatencyMs: latencyMs, concurrencyLevel });
  const round = (rps) => (rps == null ? null : Number(rps.toFixed(2)));
  return latencyRows.map((row) => ({
    key: row.key,
    metric: row.metric,
    current: round(toRps(row.current)),
    comparison: round(toRps(row.comparison)),
    measured: round(toRps(row.measured)),
    unit: 'rps',
  }));
}

/**
 * Cost per 1M tokens for classic tasks (classification / NER).
 *
 *   tokens_per_hour       = throughput_rps * avg_input_tokens * 3600
 *   cost_per_token_usd    = cost_per_hour_usd / tokens_per_hour
 *   cost_per_1m_tokens_usd = cost_per_token_usd * 1_000_000
 *
 * @param {Object} params
 * @param {number|null|undefined} params.costPerHourUsd
 * @param {number|null|undefined} params.throughputRps
 * @param {number|null|undefined} params.avgInputTokens
 * @returns {number|null} USD per 1M tokens, or null if any input is missing/zero.
 */
export function calculateCostPer1MTokensClassic({
  costPerHourUsd,
  throughputRps,
  avgInputTokens,
}) {
  if (
    costPerHourUsd === null ||
    costPerHourUsd === undefined ||
    !throughputRps ||
    !avgInputTokens
  ) {
    return null;
  }
  const tokensPerHour = throughputRps * avgInputTokens * 3600;
  return (costPerHourUsd * 1_000_000) / tokensPerHour;
}

/**
 * Cost per 1M tokens for generative tasks (summarization), where throughput
 * is measured directly in tokens/sec.
 *
 *   tokens_per_hour       = throughput_tokens_per_sec * 3600
 *   cost_per_token_usd    = cost_per_hour_usd / tokens_per_hour
 *   cost_per_1m_tokens_usd = cost_per_token_usd * 1_000_000
 *
 * @param {Object} params
 * @param {number|null|undefined} params.costPerHourUsd
 * @param {number|null|undefined} params.throughputTokensPerSec
 * @returns {number|null} USD per 1M tokens, or null if any input is missing/zero.
 */
export function calculateCostPer1MTokensGenerative({
  costPerHourUsd,
  throughputTokensPerSec,
}) {
  if (
    costPerHourUsd === null ||
    costPerHourUsd === undefined ||
    !throughputTokensPerSec
  ) {
    return null;
  }
  const tokensPerHour = throughputTokensPerSec * 3600;
  return (costPerHourUsd * 1_000_000) / tokensPerHour;
}
